import { t } from "@lingui/macro";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react";
import { templateTypography } from "@reactive-resume/schema";
import { fonts } from "@reactive-resume/utils";
import { useId, useState } from "react";

import { useResumeStore } from "@/client/stores/resume";

const families = [
  ...new Set([
    "Arial",
    "Georgia",
    "Times New Roman",
    "Verdana",
    "Inter",
    "Roboto",
    "Open Sans",
    "Lato",
    "Montserrat",
    "Poppins",
    ...Object.values(templateTypography).map(({ family }) => family),
  ]),
].sort((a, b) => a.localeCompare(b));

export const FontControls = ({ expanded = false }: { expanded?: boolean }) => {
  const id = useId();
  const metadata = useResumeStore((state) => state.resume.data.metadata);
  const locked = useResumeStore((state) => state.resume.locked);
  const setValue = useResumeStore((state) => state.setValue);
  const [sizeDraft, setSizeDraft] = useState<string | null>(null);
  const font = metadata.typography.font;
  const options = families.includes(font.family) ? families : [font.family, ...families];
  const commitSize = () => {
    const size = Number(sizeDraft);
    if (sizeDraft?.trim() && Number.isFinite(size)) {
      setValue("metadata.typography.font.size", Math.min(18, Math.max(8, size)));
    }
    setSizeDraft(null);
  };
  return (
    <fieldset disabled={locked} className="flex min-w-0 items-end gap-2 disabled:opacity-50">
      <legend className="sr-only">{t`Typographie du texte principal`}</legend>
      <label htmlFor={`${id}-family`} className={expanded ? "min-w-0 flex-1" : "w-32"}>
        {expanded && (
          <span className="mb-1.5 block text-xs font-medium text-slate-600">{t`Police du texte`}</span>
        )}
        <select
          id={`${id}-family`}
          aria-label={t`Police du texte principal`}
          value={font.custom ? font.family : "original"}
          onChange={(event) => {
            const family = event.target.value;
            if (family === "original") {
              const preset = templateTypography[metadata.template];
              if (preset)
                setValue("metadata.typography.font", {
                  ...font,
                  family: preset.family,
                  variants: preset.variants,
                  subset: "latin",
                  custom: undefined,
                });
              return;
            }
            const available = fonts.find((option) => option.family === family);
            const variants = ["regular", "italic", "500", "600", "700", "700italic"].filter(
              (variant) => !available || available.variants.includes(variant),
            );
            setValue("metadata.typography.font", {
              ...font,
              family,
              subset: "latin",
              variants,
              custom: true,
            });
          }}
          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-800 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <option value="original">{t`Police du modèle`}</option>
          {options.map((family) => (
            <option key={family} value={family}>
              {family}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={`${id}-size`} className="w-16 shrink-0">
        {expanded && (
          <span className="mb-1.5 block text-xs font-medium text-slate-600">{t`Taille`}</span>
        )}
        <input
          id={`${id}-size`}
          aria-label={t`Taille du texte principal`}
          type="number"
          min={8}
          max={18}
          step={0.5}
          value={sizeDraft ?? font.size}
          onChange={(event) => {
            const value = event.target.value;
            setSizeDraft(value);
            const size = Number(value);
            if (value.trim() && Number.isFinite(size) && size >= 8 && size <= 18) {
              setValue("metadata.typography.font.size", size);
            }
          }}
          onBlur={commitSize}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs tabular-nums text-slate-800 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        />
      </label>
      {expanded && (
        <button
          type="button"
          title={t`Typographie d’origine`}
          aria-label={t`Restaurer la typographie d’origine`}
          onClick={() => {
            const preset = templateTypography[metadata.template];
            if (!preset) return;
            setSizeDraft(null);
            setValue("metadata.typography", {
              ...metadata.typography,
              font: {
                family: preset.family,
                size: preset.size,
                subset: "latin",
                variants: preset.variants,
              },
              lineHeight: preset.lineHeight,
            });
          }}
          className="grid size-10 shrink-0 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <ArrowCounterClockwiseIcon size={17} />
        </button>
      )}
    </fieldset>
  );
};
